import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/form";
import AuthShell from "./AuthShell";
import PasswordInput from "./PasswordInput";

const schema = z.object({
  name: z.string().trim().min(1, "Enter your name").max(80),
  business_name: z.string().trim().max(80).optional(),
  email: z.string().trim().email("Enter a valid email"),
  password: z.string().min(8, "Use at least 8 characters").max(128),
});

export default function SignupPage() {
  const { user, signup } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { name: "", business_name: "", email: "", password: "" } });

  if (user) return <Navigate to={user.role === "client" ? "/me" : "/app"} replace />;

  const onSubmit = async (values) => {
    setServerError("");
    try {
      await signup(values);
      navigate("/app", { replace: true });
    } catch (e) {
      setServerError(formatApiError(e));
    }
  };

  return (
    <AuthShell
      title="Start your free trial"
      subtitle="14 days free. No card needed."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-ink underline underline-offset-4">
            Log in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
        <Field label="Your name" error={errors.name?.message}>
          {(id, err) => <Input id={id} autoComplete="name" aria-invalid={!!errors.name} aria-describedby={err} {...register("name")} />}
        </Field>
        <Field label="Business name" optional error={errors.business_name?.message}>
          {(id, err) => (
            <Input id={id} autoComplete="organization" placeholder="e.g. Iron & Oak Coaching" aria-describedby={err} {...register("business_name")} />
          )}
        </Field>
        <Field label="Email" error={errors.email?.message}>
          {(id, err) => (
            <Input id={id} type="email" autoComplete="email" aria-invalid={!!errors.email} aria-describedby={err} {...register("email")} />
          )}
        </Field>
        <Field label="Password" hint="At least 8 characters" error={errors.password?.message}>
          {(id, err) => (
            <PasswordInput id={id} autoComplete="new-password" aria-invalid={!!errors.password} aria-describedby={err} {...register("password")} />
          )}
        </Field>

        {serverError && (
          <p role="alert" className="rounded-[10px] border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700">
            {serverError}
          </p>
        )}

        <Button type="submit" size="lg" disabled={isSubmitting} className="mt-1 w-full">
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Create account
        </Button>
      </form>
    </AuthShell>
  );
}
